<template>
  <b-form class="package__form" v-on:submit.prevent="onSubmit" @reset="onReset">
    <b-card class="create-packages__helper" style="margin-bottom: 15px;">
      <span style="font-size: 16px; display: block; color: #575757;">Add Package</span>
      Use this flow to add packages to the device manager. 
      These packages can be used for various actions such as OTA jobs.
    </b-card>
    <b-form-group
      id="input-group-1"
      label="Label:"
      label-for="input-label"
      description="Enter a label for this package."
      :state="labelState"
      :invalid-feedback="labelError"
    >
      <b-form-input
        id="input-label"
        v-model="form.label"
        type="text"
        placeholder="Enter label"
        :state="labelState"
        @blur="touched.label = true"
        required
      ></b-form-input>
    </b-form-group>
    
    <b-form-group
      id="input-group-2"
      label="Version:"
      label-for="input-major"
      description="Enter a version for this package."
      :state="versionState"
      :invalid-feedback="versionError"
    >
      <div class="create-packages__version">
        <b-form-input
          id="input-major"
          v-model="form.major"
          type="number"
          min="0"
          step="1"
          placeholder=""
          :state="versionState"
          required
        ></b-form-input>
        .
        <b-form-input
          id="input-minor"
          v-model="form.minor"
          type="number"
          min="0"
          step="1"
          placeholder=""
          :state="versionState"
          required
        ></b-form-input>
        .
        <b-form-input
          id="input-build"
          v-model="form.build"
          type="number"
          min="0"
          step="1"
          placeholder=""
          :state="versionState"
          required
        ></b-form-input>
      </div>
    </b-form-group>

    <b-form-group
      id="input-group-3"
      label="File:"
      label-for="input-file"
      :description="packageDescription"
      :state="packageState"
      :invalid-feedback="packageError"
    >
      <b-form-file
        id="input-file"
        accept=".zip,application/zip,application/x-zip-compressed"
        v-model="form.file"
        :state="packageState"
        placeholder="Choose a package file or drop it here..."
        drop-placeholder="Drop package file here..."
      ></b-form-file>
    </b-form-group>

    <b-form-group
      id="input-group-4"
      label="Type:"
      label-for="input-type"
      description="Select package type."
      :state="typeState"
      :invalid-feedback="typeError"
    >
      <b-form-select v-model="form.packageType" 
        id="input-type"
        :disabled="!packageContent"
        :options="packageTypeOptions"  
        :state="typeState"
        placeholder="Select the package subtype..."
        @change="onPackageTypeSelection"></b-form-select>
    </b-form-group>

    <div class="create-package__form-buttons">
      <b-button type="reset">Reset</b-button>
      <b-button type="submit" variant="primary" :disabled="!isFormValid">Add</b-button>
    </div>
  </b-form>
</template>
<script>
import Vue from "vue";
import VueToast from "vue-toast-notification";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import xmlParser from "fast-xml-parser";
import BedManagerService from "../BedManager.service";
import DeviceManagerAPIService from "../../../shared/services/api/DeviceManager.service";

const moment = require("moment");

Vue.use(VueToast);
Vue.use(Loading);

const DEFAULT_PACKAGE_SIZE_LIMIT = 250000000;
const LABEL_MAX_LENGTH = 100;
// Browsers report zip files with different MIME types (and sometimes none at all).
const ZIP_MIME_TYPES = ["", "application/zip", "application/x-zip-compressed", "application/x-zip", "multipart/x-zip"];

export default {
  name: "CreatePackages",
  components: { },
  data() {
    return {
      console: console,
      moment: moment,
      bedManagerService: BedManagerService,
      deviceManagerAPIService: DeviceManagerAPIService,

      latestPackage: null,
      allPackages: [],
      form: {
        label: "",
        major: "",
        minor: "",
        build: "",
        file: null,
        packageType: null,
      },
      touched: { label: false },
      signedUrl: null,
      // status: null | 'checking' | 'valid' | 'invalid'
      packageCheck: { status: null, message: "" },
      packageContent: null,
      packageInfo: {}
    };
  },
  computed: {
    formFile() {
      return this.form.file;
    },
    configuration() {
      return this.bedManagerService.configuration;
    },
    packageSizeLimit() {
      return this.configuration.awsPackageSizeLimit || DEFAULT_PACKAGE_SIZE_LIMIT;
    },
    trimmedLabel() {
      return (this.form.label || "").trim();
    },
    labelError() {
      if (!this.trimmedLabel) return "Label is required.";
      if (this.trimmedLabel.length > LABEL_MAX_LENGTH) return `Label must be ${LABEL_MAX_LENGTH} characters or fewer.`;
      return "";
    },
    labelState() {
      if (!this.touched.label && !this.form.label) return null;
      return !this.labelError;
    },
    versionParts() {
      return [this.form.major, this.form.minor, this.form.build].map(part => String(part === null || part === undefined ? "" : part).trim());
    },
    formVersion() {
      return this.versionParts.map(Number).join(".");
    },
    versionError() {
      if (this.versionParts.some(part => !/^\d+$/.test(part))) {
        return "Major, minor and build must be whole numbers (0 or greater).";
      }
      if (this.allPackages.find(pkg => pkg.version === this.formVersion)) {
        return `Version ${this.formVersion} already exists.`;
      }
      return "";
    },
    versionState() {
      if (this.versionParts.every(part => part === "")) return null;
      return !this.versionError;
    },
    fileError() {
      const file = this.form.file;
      if (!file) return "Package file is required.";
      if (!/\.zip$/i.test(file.name) || !ZIP_MIME_TYPES.includes(file.type)) return "Package must be a .zip file.";
      if (!file.size) return "Package file is empty.";
      if (file.size > this.packageSizeLimit) {
        return `Package size exceeds ${Math.round(this.packageSizeLimit / 1000000)} MB.`;
      }
      return "";
    },
    packageError() {
      if (this.fileError) return this.fileError;
      if (this.packageCheck.status === "invalid") return this.packageCheck.message;
      return "";
    },
    packageState() {
      if (!this.form.file || this.packageCheck.status === "checking") return null;
      return !this.packageError;
    },
    packageDescription() {
      return this.packageCheck.status === "checking" ? "Checking package contents..." : "Select package file.";
    },
    packageTypeOptions() {
      const packages = Array.isArray(this.configuration.packages) ? this.configuration.packages : [];
      // For now, only allow firmware packages to be selected. In the future, we can add more package types here.
      return [
        { text: 'Select Package Type', value: null },
        ...packages
          .filter((item) => item === 'Firmware')
          .map((item) => ({ text: item, value: item })),
      ];
    },
    typeError() {
      if (!this.form.packageType) return "Package type is required.";
      if (!this.packageTypeOptions.some(option => option.value && option.value === this.form.packageType)) {
        return "Select a valid package type.";
      }
      return "";
    },
    typeState() {
      if (!this.packageContent) return null;
      return !this.typeError;
    },
    isFormValid() {
      return !this.labelError &&
        !this.versionError &&
        !this.packageError &&
        !this.typeError &&
        this.packageCheck.status === "valid" &&
        Boolean(this.signedUrl) &&
        this.signedUrl !== "gathering";
    }
  },
  watch: {
    async formFile(file) {
      this.form.packageType = null;
      this.packageInfo = {};
      this.packageContent = null;
      this.signedUrl = null;
      this.packageCheck = { status: null, message: "" };

      if (!file || !file.name || this.fileError) return;

      await Promise.all([this.loadPackageContent(file), this.loadSignedUrl(file)]);
    }
  },
  mounted() {
    this.fetchAllPackages();
  },
  unmounted() {},
  methods: {
   async fetchAllPackages() {
      try {
        const res = await this.deviceManagerAPIService.getPackageVersions({ queryFilter: 'all', sortProperty: 'created', sortDirection: 'desc' });
        this.allPackages = res && Array.isArray(res.items) ? res.items : [];
      } catch (e) {
        this.allPackages = [];
      }
      // Items are sorted by created desc, so the first entry is the latest package.
      this.latestPackage = this.allPackages.length ? this.allPackages[0] : null;
      this.determineNextVersion();
    },
    determineNextVersion() {
      if (this.latestPackage && this.latestPackage.version) {
        const [major, minor, build] = this.latestPackage.version.split('.').map(Number);
        const existingVersions = new Set(this.allPackages.map(pkg => pkg.version));
        let nextBuild = (build || 0) + 1;
        while (existingVersions.has(`${major || 0}.${minor || 0}.${nextBuild}`)) nextBuild++;
        this.form.major = major || 0;
        this.form.minor = minor || 0;
        this.form.build = nextBuild;
      }
    },
    async loadPackageContent(file) {
      this.packageCheck = { status: "checking", message: "" };
      try {
        const content = await this.bedManagerService.extractZipContent(file);
        if (this.form.file !== file) return;
        this.packageContent = content;
        this.packageCheck = { status: "valid", message: "" };
      } catch (e) {
        if (this.form.file !== file) return;
        this.packageCheck = { status: "invalid", message: "Package is not a valid or readable zip file." };
      }
    },
    async loadSignedUrl(file) {
      this.signedUrl = "gathering";
      let res = null;
      try {
        res = await this.deviceManagerAPIService.fetchSignedUrl(file.name);
      } catch (e) {
        res = null;
      }
      if (this.form.file !== file) return;
      if (res && res.message) {
        this.signedUrl = res.message;
        return;
      }
      this.signedUrl = null;
      this.$toast.open({
        message: "Could not prepare the package upload. Please select the file again.",
        type: "error",
        duration: 5000,
      });
      this.onReset();
    },
    readManifestModules(xml) {
      if (typeof xml !== "string" || xmlParser.validate(xml) !== true) return null;
      const manifest = this.bedManagerService.parseXml(xml);
      const modules = manifest && manifest.softwareModules && manifest.softwareModules.softwareModule;
      const list = Array.isArray(modules) ? modules : (modules ? [modules] : []);
      if (!list.length || list.some(module => !module || !module.id || !module.partNumber)) return null;
      return list;
    },
    validateFirmwarePackage(content) {
      const gatewayModules = this.readManifestModules(content["manifest.xml"]);
      if (!gatewayModules) {
        return { error: "Not a valid Firmware package: manifest.xml is missing or invalid." };
      }
      const updateZip = content.Procuity && content.Procuity["update.zip"];
      const updateModules = this.readManifestModules(updateZip && updateZip["manifest.xml"]);
      if (!updateModules) {
        return { error: "Not a valid Firmware package: Procuity/update.zip/manifest.xml is missing or invalid." };
      }
      const packageInfo = {};
      [...gatewayModules, ...updateModules].forEach(module => {
        packageInfo[String(module.id).replace(/\./g, "_")] = module.partNumber;
      });
      return { packageInfo };
    },
    onPackageTypeSelection(value) {
      this.packageInfo = {};
      if (!this.packageContent) return;
      if (value === "Firmware") {
        const result = this.validateFirmwarePackage(this.packageContent);
        if (result.error) {
          this.packageCheck = { status: "invalid", message: result.error };
          return;
        }
        this.packageInfo = result.packageInfo;
      }
      this.packageCheck = { status: "valid", message: "" };
    },
    async onSubmit() {
      if (!this.isFormValid) return;
      const willProceed = 
        confirm("Before submitting this package, it is critical that you ensure that the correct package files were selected before submitting. Press ok to continue, otherwise press cancel.");
      const action = {
        label: 'Processing package upload. Please wait...',
      };
      if (willProceed) {
        const label = this.trimmedLabel;
        const version = this.formVersion;
        const packageType = this.form.packageType;
        const file = this.form.file;
        const signedUrl = this.signedUrl;
        const packageInfo = this.packageInfo;

        action.action = this.deviceManagerAPIService.postPackageWithSignedUrl(file, signedUrl).then((uploadResponse) => {
          if (!uploadResponse || !uploadResponse.ok) {
            throw new Error(`Package upload failed${uploadResponse ? ` with status ${uploadResponse.status}` : ""}.`);
          }
          this.deviceManagerAPIService.postPackage({
            signedUrl,
            label,
            version,
            packageType,
            fileName: file.name,
          }).then(() => {
            action.label = 'Package uploaded. The package will show as available shortly.';

            // adding file info before saving into db
            if (packageInfo) {
              packageInfo.label = label;
              packageInfo.version = version;
              this.deviceManagerAPIService.postPackageDetails(packageInfo).then();
            }
            
            setTimeout(() => {
              this.bedManagerService.actions.splice(this.bedManagerService.actions.indexOf(action), 1);
            }, 5000);
          });
        }).catch(() => {
          setTimeout(() => {
            action.label = 'Package failed to upload.';
            setTimeout(() => {
              this.bedManagerService.actions.splice(this.bedManagerService.actions.indexOf(action), 1);
            }, 5000);
          }, 3000);
        });
        this.bedManagerService.actions = action;
      }  
      this.$emit('exit', true);
    },
    onReset() {
      this.form = {
        label: "",
        major: "",
        minor: "",
        build: "",
        file: null,
        packageType: null,
      }
      this.touched = { label: false };
      this.determineNextVersion();
      this.packageInfo = {};
    },
  },
};
</script>

<style lang="scss">
  #add-item-modal {
    label {
      color: #404040;
    }
  }

  .package__form {
    > .form-group {
      padding: 0 15px;
      margin: 0 15px;
    }

    .card {
      border: none;
    }
  }

  .create-packages__helper {
    background: #f2f2f2;
    color: #a9a9a9; 
    font-size: 12px;
    border: none;
    border-bottom: 1px solid rgb(210 210 210 / 13%);
  }

  .create-packages__version {
    display: flex; 
    gap: 2px;
    align-items: center;

    > input {
      height: 30px;
      width: 70px;
      padding: 2px;
      padding-left: 6px;

      // Bootstrap reserves space for the state icon with an !important padding-right,
      // which leaves no room for the value in these narrow inputs.
      &.is-valid,
      &.is-invalid {
        padding-right: 2px !important;
        background-image: none;
      }
    }
  }

  .create-package__form-buttons {
    display: flex;
    padding: 15px 30px;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;

    .btn-secondary {
      background-color: #fff;
      color: #848484;
    }
  }
</style>
